import type { ObjectTypename } from '@precon-tools-suite-by-shared/precon-graphql-types';
import { GraphQLError } from 'graphql';

/**
 * Put all the options for this library that must be set in the `main.ts` of the application here.
 */
export interface PreconToastsGlobalOptions {
    closable?: boolean;
    life?: number;
    unauthorized?: {
        summary: string;
        life: number;
    };
}

/**
 * This is the type of the options object used within the library.
 */
export type NormalizedPreconToastsGlobalOptions =
    Required<PreconToastsGlobalOptions>;

/**
 * Function to convert the input options (`PreconToastsGlobalOptions`) to the normalized
 * options (`NormalizedPreconToastsGlobalOptions`).
 *
 * It usually just sets the default values for the library.
 *
 * @param options The input options as set in the application that uses this library.
 */
export function normalizePreconToastsOptions(
    options: PreconToastsGlobalOptions = {}
): NormalizedPreconToastsGlobalOptions {
    return {
        closable: options.closable ?? false,
        life: options.life ?? 5000,
        unauthorized: {
            summary: 'Onbevoegd!',
            life: options.life ?? 2000,
            ...(options.unauthorized ?? {}),
        },
    };
}

/**
 * Represents the available types of actions the message is referring to.
 */
export type actionMessageType = 'create' | 'update' | 'delete' | 'connect';
/**
 * Represents the available types of messages.
 */
export type messageType = 'create' | 'update' | 'delete' | 'connect' | 'error';

/**
 * Represents the available types of messages.
 */
interface messageOptions {
    messageType: messageType;
}

/**
 * Represents the options for a success message.
 */
export interface actionMessageOptions extends messageOptions {
    messageType: actionMessageType;
    entityTypename?: ObjectTypename;
    entityName: string | null | undefined;
}

/**
 * Represents the options for an error message.
 */
export interface errorMessageOptions extends messageOptions {
    messageType: 'error';
    actionType: actionMessageType;
    error: readonly GraphQLError[] | Error | string;
}
